"use client";

import type { SessionCountdown } from "@/hooks/useSessionCountdown";
import { cn } from "@/lib/utils";

// ============================================================================
// Imminence du direct. Présentationnel : l'état vient de useSessionCountdown.
//   • LivePulse — la pastille rouge qui bat. Elle flotte en haut (barre de
//     date) et en bas (barre CTA) : c'est elle qui fait lire la page comme un
//     événement en cours et non comme une page de vente.
//   • Countdown — compte à rebours COMPACT, sur une ligne. Les grosses cartes
//     chiffrées mangeaient un tiers du premier écran pour une information qui
//     se lit en un coup d'œil.
// ============================================================================

const pad = (n: number) => String(n).padStart(2, "0");

export function LivePulse({ className }: { className?: string }) {
  return (
    <span className={cn("relative flex h-2 w-2 shrink-0", className)} aria-hidden>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500/70" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
    </span>
  );
}

/** « 03j 19:42:15 » — dense, tabulaire, sans saut de largeur à chaque seconde. */
function Digits({ c, className }: { c: SessionCountdown; className?: string }) {
  return (
    <span className={cn("font-semibold tabular-nums", className)}>
      {c.days > 0 && `${c.days}j `}
      {pad(c.hours)}:{pad(c.minutes)}:{pad(c.seconds)}
    </span>
  );
}

export function Countdown({
  c,
  size = "lg",
  className,
}: {
  c: SessionCountdown;
  size?: "lg" | "sm";
  className?: string;
}) {
  // Avant hydratation : on réserve la hauteur sans afficher d'heure, sinon le
  // HTML serveur et le HTML client divergent.
  if (!c.ready) {
    return (
      <div aria-hidden className={cn(size === "lg" ? "h-11" : "h-5", className)} />
    );
  }

  if (c.live) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-2 font-semibold text-red-600",
          size === "lg"
            ? "rounded-full border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm"
            : "text-xs",
          className
        )}
      >
        <LivePulse />
        {size === "lg" ? "C’est en direct maintenant" : "En direct"}
      </span>
    );
  }

  if (size === "sm") {
    return <Digits c={c} className={cn("text-xs text-ink", className)} />;
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-white px-4 py-2.5 shadow-sm",
        className
      )}
    >
      <LivePulse />
      <span className="text-sm font-medium text-ink/60">Ça commence dans</span>
      <Digits c={c} className="text-base text-ink" />
    </span>
  );
}
